/**
 * Payment flow guard
 *
 * Used to block signed-URL refresh (page reload) while the user is in a payment flow:
 * tip, purchase, or subscribe, or an active Matrix call. Prevents reload during CCBill
 * or other payment modals, and during WebRTC (minimize / return).
 *
 * Uses sessionStorage so flags are tab-scoped and do not leak across tabs.
 * Payment and call use separate keys so closing a tip modal during a call does not
 * clear the call guard.
 */

const PAYMENT_STORAGE_KEY = 'paymentFlowActive';
const CALL_STORAGE_KEY = 'callFlowActive';

/**
 * Set whether a payment flow (tip / purchase / subscribe) is currently active.
 * Call with true when opening a payment modal, false when closing it.
 * @param {boolean} active
 */
export function setPaymentFlowActive(active) {
  if (typeof window === 'undefined') return;
  try {
    if (active) {
      sessionStorage.setItem(PAYMENT_STORAGE_KEY, '1');
    } else {
      sessionStorage.removeItem(PAYMENT_STORAGE_KEY);
    }
  } catch {
    // Ignore in private mode or when storage is disabled
  }
}

/**
 * Set whether an active voice/video call is in progress (Matrix CallingScreen).
 * @param {boolean} active
 */
export function setCallFlowActive(active) {
  if (typeof window === 'undefined') return;
  try {
    if (active) {
      sessionStorage.setItem(CALL_STORAGE_KEY, '1');
    } else {
      sessionStorage.removeItem(CALL_STORAGE_KEY);
    }
  } catch {
    // Ignore in private mode or when storage is disabled
  }
}

/**
 * Returns true if a payment flow is currently active (tip, purchase, or subscribe).
 * @returns {boolean}
 */
export function isPaymentFlowActive() {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(PAYMENT_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * @returns {boolean}
 */
export function isCallFlowActive() {
  if (typeof window === 'undefined') return false;
  try {
    return sessionStorage.getItem(CALL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * True while payment or an active call should block signed-URL page reload.
 * @returns {boolean}
 */
export function shouldBlockSignedUrlRefresh() {
  return isPaymentFlowActive() || isCallFlowActive();
}
